<template>
  <div data-testid="header-profile-menu-trigger">
    <Popover :items="items" size="1" variant="blank">
      <template #header>
        <Text size="3" weight="normal" variant="accent">{{ user?.email ?? '' }}</Text>
      </template>
      <template #trigger="{ toggle }">
        <Button
          variant="blank"
          size="3"
          square
          :aria-label="$t('modules.campaigns.layout.header.openProfileMenu')"
          @click="toggle"
        >
          <ButtonPrefix>
            <Avatar :name="user?.email ?? ''" variant="outline" color="default" size="2" />
          </ButtonPrefix>
        </Button>
      </template>
    </Popover>
    <FakeLoginForm v-if="canImpersonate" ref="fakeLoginFormRef" stay-on-current-url />
  </div>
</template>

<script setup lang="ts">
import { Avatar, Button, ButtonPrefix, Popover, type PopoverItem, Text } from '@hprofits/ui';
import { runDetached } from '@hprofits/utils';
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

import { isNewInterfaceEnabled, openNewInterface } from '@/auth/openNewInterface';
import FakeLoginForm from '@/components/FakeLoginForm.vue';
import { useAuth } from '@/composables/auth/useAuth';
import { useLocaleSwitcher } from '@/composables/useLocaleSwitcher';
import { t } from '@/i18n';
import * as auth from '@/utils/auth';
import { buildRoleSwitchHref } from '@/utils/role';

defineOptions({ name: 'HeaderProfileMenu' });

const props = defineProps<{
  campaignOwnerEmail?: string | null;
  campaignOwnerId?: number | null;
}>();

const route = useRoute();
const { user, role, availableRoles, isAdmin, isImpersonatingTab, switchRole, switchUser, exitImpersonation, logout } =
  useAuth();

const { locale, choices: localeChoices, switchLocale } = useLocaleSwitcher();

const IMPERSONATION_ROLES: ReadonlyArray<string> = ['admin', 'advert-admin', 'manager'];
const canImpersonate = computed<boolean>(
  () => !isImpersonatingTab.value && availableRoles.value.some((r) => IMPERSONATION_ROLES.includes(r)),
);

const fakeLoginFormRef = ref<InstanceType<typeof FakeLoginForm> | null>(null);
const openFakeLogin = (): void => {
  fakeLoginFormRef.value?.openForm();
};

const canImpersonateCampaignOwner = computed<boolean>(
  () =>
    canImpersonate.value &&
    props.campaignOwnerId != null &&
    props.campaignOwnerEmail != null &&
    props.campaignOwnerEmail !== user.value?.email,
);

const loginAsCampaignOwner = (): void => {
  if (props.campaignOwnerId == null) {
    return;
  }
  fakeLoginFormRef.value?.fakeLoginByUserId(props.campaignOwnerId);
};

const titleCase = (raw: string): string =>
  raw
    .split('-')
    .map((part) => part.slice(0, 1).toUpperCase() + part.slice(1))
    .join(' ');

const roleItems = computed<Array<PopoverItem>>(() => {
  if (role.value === 'advertiser') {
    return [];
  }
  if (!isAdmin.value && !isImpersonatingTab.value) {
    return [];
  }
  return availableRoles.value.map<PopoverItem>((r) => {
    const isCurrent = r === role.value;
    return {
      label: isCurrent
        ? t('modules.campaigns.layout.header.roleCurrent', { role: titleCase(r) })
        : t('modules.campaigns.layout.header.roleSwitch', { role: titleCase(r) }),
      disabled: isCurrent,
      href: isCurrent ? undefined : buildRoleSwitchHref(r),
      onClick: isCurrent ? undefined : () => switchRole(r),
    };
  });
});

const languageItems = computed<Array<PopoverItem>>(() =>
  localeChoices.map<PopoverItem>(({ value, label }) => ({
    label,
    selected: value === locale.value,
    disabled: value === locale.value,
    // Switching may have to fetch the locale's catalogue chunk, and `PopoverItem.onClick` is a
    // `void` signature this component does not own, so the rejection is logged rather than dropped.
    onClick: () => {
      runDetached(switchLocale(value));
    },
  })),
);

const userItems = computed<Array<PopoverItem>>(() => {
  if (isImpersonatingTab.value) {
    return [];
  }
  let stored: Record<string, string> = {};
  try {
    stored = JSON.parse(localStorage.getItem('jwt-tokens') ?? '{}') as Record<string, string>;
  } catch {
    stored = {};
  }
  const fakeLogins = auth.readFakeLoginsList();
  const currentEmail = user.value?.email;
  return Object.keys(stored)
    .filter((email) => email !== currentEmail && !fakeLogins.includes(email))
    .map<PopoverItem>((email) => ({
      label: `Switch user: ${email}`,
      onClick: () => switchUser(email),
    }));
});

// divider: true on a PopoverItem renders MenuDivider BEFORE that item.
// Skip divider on the very first item — popover header already provides a separator.
const items = computed<Array<PopoverItem>>(() => {
  const fakeLogin = auth.readFakeLogin();
  const impersonationSection: Array<PopoverItem> = isImpersonatingTab.value
    ? [
        {
          // Two messages, not an interpolated fallback word: the generic case has to read as a
          // sentence of its own once the account name is not there to carry it.
          label:
            fakeLogin.admin === undefined
              ? t('modules.campaigns.layout.header.returnToAccount')
              : t('modules.campaigns.layout.header.returnTo', { account: fakeLogin.admin }),
          onClick: exitImpersonation,
        },
      ]
    : [];
  const adminSection: Array<PopoverItem> = canImpersonate.value
    ? [
        ...(canImpersonateCampaignOwner.value
          ? [
              {
                label: t('modules.campaigns.layout.header.loginAsOwner', { email: props.campaignOwnerEmail }),
                onClick: loginAsCampaignOwner,
              },
            ]
          : []),
        { label: t('modules.campaigns.layout.header.loginAs'), onClick: openFakeLogin },
      ]
    : [];
  const sections = [roleItems.value, userItems.value, impersonationSection, adminSection, languageItems.value].filter(
    (s) => s.length > 0,
  );
  const sectioned = sections.flatMap((section, sectionIdx) =>
    section.map((item, i) => (i === 0 && sectionIdx > 0 ? { ...item, divider: true } : item)),
  );
  // Hidden in a "login as" tab: the new app's session is per browser, it would switch every tab to that user.
  const newInterface: Array<PopoverItem> =
    isImpersonatingTab.value || !isNewInterfaceEnabled()
      ? []
    : [
        {
          label: t('modules.campaigns.layout.header.openNewInterface'),
          onClick: () => openNewInterface(route.path),
          divider: true,
        },
      ];
  return [
    ...sectioned,
    ...newInterface,
    { label: t('modules.campaigns.layout.header.signOut'), onClick: logout, divider: newInterface.length === 0 },
  ];
});
</script>
